import React, { useState } from 'react';
import ImageCropper from './ImageCropper';
import { API_URL } from '../config';
import { useNotification } from './NotificationSystem';
import './UserProfileEdit.css';

function UserProfileEdit({ user, onClose, onUpdate, token }) {
  const { showSuccess, showError, confirm } = useNotification();
  const [username, setUsername] = useState(user.username || '');
  const [status, setStatus] = useState(user.status || 'online');
  const [saving, setSaving] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      showError('Please select a valid image file (JPEG, PNG, or WebP)');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      showError('Image size must be less than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleCropComplete = async (croppedBlob) => {
    try {
      setUploadingAvatar(true);
      setImagePreview(null);

      const formData = new FormData();
      formData.append('avatar', croppedBlob, 'avatar.jpg');

      const response = await fetch(`${API_URL}/api/users/avatar`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        showSuccess('Avatar updated successfully!');
        // Update user with new avatar
        onUpdate({ ...user, avatar_url: result.avatar_url });
      } else {
        const error = await response.json();
        showError(error.error || 'Failed to upload avatar');
      }
    } catch (error) {
      console.error('Error uploading avatar:', error);
      showError('Failed to upload avatar');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await fetch(`${API_URL}/api/users/profile`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ username, status })
      });

      if (response.ok) {
        const updated = await response.json();
        onUpdate(updated);
        onClose();
      } else {
        const error = await response.json();
        showError(error.error || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      showError('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {imagePreview && (
        <ImageCropper
          image={imagePreview}
          onCropComplete={handleCropComplete}
          onCancel={() => setImagePreview(null)}
          aspectRatio={1}
        />
      )}
      
      <div className="user-profile-edit-backdrop" onClick={onClose}>
        <div className="user-profile-edit-modal" onClick={(e) => e.stopPropagation()}>
          <div className="profile-edit-header">
            <h2>Edit Profile</h2>
            <button className="close-btn" onClick={onClose}>✕</button>
          </div>

          <div className="profile-edit-content">
            <div className="profile-edit-section">
              <div className="profile-avatar-container">
                {user.avatar_url ? (
                  <img 
                    src={`${API_URL}${user.avatar_url}`} 
                    alt="Avatar" 
                    className="profile-avatar-large"
                  />
                ) : (
                  <div className="profile-avatar-large">
                    {username[0]?.toUpperCase() || user.username[0]?.toUpperCase()}
                  </div>
                )}
                <div className="avatar-upload-overlay">
                  <label htmlFor="avatar-upload" className="avatar-upload-btn">
                    {uploadingAvatar ? 'Uploading...' : 'Change Avatar'}
                  </label>
                  <input
                    id="avatar-upload"
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                    disabled={uploadingAvatar}
                  />
                </div>
              </div>
            </div>

            <div className="profile-edit-section">
              <label htmlFor="username">Display Name</label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter your display name"
                maxLength={32}
              />
              <span className="input-hint">This is how others will see you</span>
            </div>

            <div className="profile-edit-section">
              <label htmlFor="status">Status</label>
              <select
                id="status"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value="online">🟢 Online</option>
                <option value="idle">🟡 Idle</option>
                <option value="dnd">🔴 Do Not Disturb</option>
                <option value="invisible">⚫ Invisible</option>
              </select>
              <span className="input-hint">Control your visibility to others</span>
            </div>
          </div>

          <div className="profile-edit-footer">
            <button className="btn-cancel" onClick={onClose}>Cancel</button>
            <button 
              className="btn-save" 
              onClick={handleSave}
              disabled={saving || !username.trim()}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default UserProfileEdit;
